import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { GroupConfigHolding } from '../domain/groupConfig';

import type { Address } from '@/common/domain/Address';
import type { Contact } from '@/common/domain/Contact';

export interface HoldingConfigSlice {
    __component: 'holding.holding';
    name?: string;
    logo?: string;
    address?: Address;
    contact?: Contact;
    commercialInfos?: { companyNumber: string};
    id?: number;
}

export const initialPendingHoldingConfigState: HoldingConfigSlice = {
    __component: 'holding.holding',
    address: {
        address: '',
        city: '',
        country: '',
        zip: ''
    },
    commercialInfos: {
        companyNumber: ''
    },
    contact: {
        email: '',
        fax: '',
        firstname: '',
        phone: '',
        surname: ''
    },
    logo: '',
    name: ''
};

const initialState: HoldingConfigSlice = initialPendingHoldingConfigState;

const holdingConfigSlice = createSlice({
    initialState: initialState,
    name: 'holding-config',
    reducers: {
        clearHoldingConfig: () => {
            return initialState;
        },
        clearPendingGroupConfigHolding: () => {
            return initialState;
        },
        initializeHoldingConfig: () => {
            return initialState;      
        },
        setHoldingConfig: (state, action: PayloadAction<GroupConfigHolding>) => {
            return action.payload;
        },
        setPendingGroupConfigHolding: (state, action: PayloadAction<GroupConfigHolding>) => {
            return action.payload;
        },
        updatePendingGroupConfigHoldingProps(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;
    
            // Check if superGroupConfig exists
            if (!state) {
                return state; // Return current state if superGroupConfig doesn't exist
            }
    
            // Split field into nested parts
            const fieldParts = field.split('.');
    
            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)
    
                /*
                 * Check if top-level field exists 
                 * TODO : either set all possible fields in initialState or find another way to check ot topLevel field makes sense
                 */
                if (typeof (initialState[topLevelField as keyof HoldingConfigSlice]) === 'undefined') { 
                    return state; // Return current state if top-level field doesn't exist
                }
    
                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...(state[topLevelField as keyof HoldingConfigSlice] as Record<string, unknown>),
                    [nestedFields.join('.')]: value // Update the nested field
                };
    
                // Create a new superGroupConfig object with the updated nested fields
                const updatedPendingGroupConfigHolding = {
                    ...state,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };
    
                // Return the new state with updated superGroupConfig
                state = updatedPendingGroupConfigHolding;
    
                return state;
            } else {
    
                // If field is not nested, update it directly
                state = {
                    ...state,
                    [field]: value // Update the field
                };
                return state;
            }
        },
    },
});

export const { clearHoldingConfig, clearPendingGroupConfigHolding, setHoldingConfig, initializeHoldingConfig, setPendingGroupConfigHolding, updatePendingGroupConfigHoldingProps } = holdingConfigSlice.actions;
export default holdingConfigSlice.reducer;